/**
 * Multi-dimensional comparison of environment state between two benchmark suite runs.
 *
 * Compares knobs, feature flags, prompts, model config, workflow flows,
 * and benchmark config settings — showing summary badges and diff tables per dimension.
 *
 * @module config-comparison/ConfigComparisonView
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import { Box, Flex, Text, TagBadge } from '@chatsdet/ui';
import { type BenchmarkRun, formatDateTime } from '../../useBenchmarkData';
import {
  type DiffRow,
  diffSnapshots,
  countChanges,
  countRecordChanges,
  toRecord,
} from './diff-utils';
import { DiffTable } from './DiffTable';
import { PromptDiffSection } from './PromptDiffSection';
import { ModelConfigDiffSection } from './ModelConfigDiffSection';
import { WorkflowDiffSection } from './WorkflowDiffSection';
import { BenchmarkConfigDiffSection } from './BenchmarkConfigDiffSection';

interface DiffSummary {
  hasChanges: boolean;
  dimensions: Record<string, { changed: number; total: number }>;
}

/** Build summary counts from pre-computed diff rows and env snapshots */
function buildDiffSummary(
  knobsDiff: DiffRow[],
  flagsDiff: DiffRow[],
  runA: BenchmarkRun,
  runB: BenchmarkRun
): DiffSummary {
  const dims: DiffSummary['dimensions'] = {};

  dims.knobs = { changed: countChanges(knobsDiff), total: knobsDiff.length };
  dims.featureFlags = { changed: countChanges(flagsDiff), total: flagsDiff.length };

  const envA = runA.environmentSnapshot;
  const envB = runB.environmentSnapshot;
  if (envA && envB) {
    dims.prompts = countRecordChanges(
      envA.prompts,
      envB.prompts,
      (a, b) => a.version === b.version && a.contentHash === b.contentHash
    );

    const modelDiff = diffSnapshots(toRecord(envA.modelConfig), toRecord(envB.modelConfig));
    dims.modelConfig = { changed: countChanges(modelDiff), total: modelDiff.length };

    dims.workflows = countRecordChanges(
      envA.workflows,
      envB.workflows,
      (a, b) => a.snapshotHash === b.snapshotHash && a.snapshotVersion === b.snapshotVersion
    );

    const cfgDiff = diffSnapshots(toRecord(envA.benchmarkConfig), toRecord(envB.benchmarkConfig));
    dims.benchmarkConfig = { changed: countChanges(cfgDiff), total: cfgDiff.length };
  }

  const hasChanges = Object.values(dims).some((d) => d.changed > 0);
  return { hasChanges, dimensions: dims };
}

export function useConfigDiffBadge(
  runA: BenchmarkRun | null,
  runB: BenchmarkRun | null
): DiffSummary | null {
  return React.useMemo(() => {
    if (!runA || !runB) return null;
    const knobsDiff = diffSnapshots(runA.knobsSnapshot, runB.knobsSnapshot);
    const flagsDiff = diffSnapshots(runA.featureFlagsSnapshot, runB.featureFlagsSnapshot);
    return buildDiffSummary(knobsDiff, flagsDiff, runA, runB);
  }, [runA, runB]);
}

interface ConfigComparisonViewProps {
  runA: BenchmarkRun;
  runB: BenchmarkRun;
  /** Pre-computed summary from useConfigDiffBadge to avoid redundant diff computation */
  summary?: DiffSummary;
}

const dimensionLabels: Record<string, string> = {
  knobs: 'Knobs',
  featureFlags: 'Flags',
  prompts: 'Prompts',
  modelConfig: 'Model',
  workflows: 'Flows',
  benchmarkConfig: 'Config',
};

export function ConfigComparisonView({
  runA,
  runB,
  summary: precomputed,
}: ConfigComparisonViewProps) {
  const labelA = formatDateTime(runA.startedAt);
  const labelB = formatDateTime(runB.startedAt);

  const knobsDiff = React.useMemo(
    () => diffSnapshots(runA.knobsSnapshot, runB.knobsSnapshot),
    [runA.knobsSnapshot, runB.knobsSnapshot]
  );

  const flagsDiff = React.useMemo(
    () => diffSnapshots(runA.featureFlagsSnapshot, runB.featureFlagsSnapshot),
    [runA.featureFlagsSnapshot, runB.featureFlagsSnapshot]
  );

  const computed = React.useMemo(
    () => buildDiffSummary(knobsDiff, flagsDiff, runA, runB),
    [knobsDiff, flagsDiff, runA, runB]
  );

  const summary = precomputed ?? computed;

  const envA = runA.environmentSnapshot;
  const envB = runB.environmentSnapshot;
  const hasEnvSnapshot = !!(envA && envB);

  return (
    <Box className="space-y-4">
      {/* Summary badges */}
      <Flex gap="sm" wrap="wrap" align="center">
        {summary.hasChanges ? (
          <TagBadge name="Configuration differs" color="yellow" hideDot className="text-xs" />
        ) : (
          <TagBadge name="Configuration identical" color="blue" hideDot className="text-xs" />
        )}
        {Object.entries(summary.dimensions).map(
          ([key, dim]) =>
            dim.changed > 0 && (
              <TagBadge name={<>{dimensionLabels[key] ?? key}: {dim.changed} changed</>} color="yellow" hideDot key={key} className="text-xs" />
            )
        )}
      </Flex>

      {/* Knobs (always available) */}
      <div>
        <Text weight="medium" className="mb-2">
          Knobs
        </Text>
        <DiffTable rows={knobsDiff} labelA={labelA} labelB={labelB} />
      </div>

      {/* Feature Flags (always available) */}
      {flagsDiff.length > 0 && (
        <div>
          <Text weight="medium" className="mb-2">
            Feature Flags
          </Text>
          <DiffTable rows={flagsDiff} labelA={labelA} labelB={labelB} />
        </div>
      )}

      {/* Environment snapshot dimensions */}
      {hasEnvSnapshot ? (
        <>
          <PromptDiffSection
            promptsA={envA.prompts}
            promptsB={envB.prompts}
            labelA={labelA}
            labelB={labelB}
          />
          <ModelConfigDiffSection
            configA={envA.modelConfig}
            configB={envB.modelConfig}
            labelA={labelA}
            labelB={labelB}
          />
          <WorkflowDiffSection
            workflowsA={envA.workflows}
            workflowsB={envB.workflows}
            labelA={labelA}
            labelB={labelB}
          />
          <BenchmarkConfigDiffSection
            configA={envA.benchmarkConfig}
            configB={envB.benchmarkConfig}
            labelA={labelA}
            labelB={labelB}
          />
        </>
      ) : (
        <Text className="text-muted-foreground text-xs">
          Extended comparison data not available for these runs.
        </Text>
      )}
    </Box>
  );
}
